import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { getJson, signInUrl, useMe } from '../api.ts';
import { rolesText } from '../format.ts';
import './guides.css';

interface GuideCard {
  key: string;
  title: string;
  blurb: string;
  url: string | null;
}

const PUBLIC_CARD: GuideCard = {
  key: 'mentors',
  title: 'Mentors & Students',
  blurb: 'Quick start: sign in, browse and apply for topics, track your applications, suggest your own.',
  url: '/docs/mentors-students.html',
};

/** The old card icons, by guide key; meetings as its menu tab. */
const ICON: Record<string, string> = { mentors: '🎓', meetings: '🗓', manager: '🗂', admin: '🛠' };

function CardBody({ g }: { g: GuideCard }) {
  return (
    <>
      <span className="gd-go">{g.url ? 'Open ↗' : 'Open →'}</span>
      <h2>{ICON[g.key] && <span aria-hidden="true">{ICON[g.key]} </span>}{g.title}</h2>
      <p>{g.blurb}</p>
      {g.url ? <span className="gd-tag pub">Public — shareable link</span> : <span className="gd-tag">Signed-in only</span>}
    </>
  );
}

/** /guides — the manuals this person may read. Visitors see the public one only. */
export function GuidesPage() {
  const me = useMe();
  const member = me.data?.member ? me.data : null;
  const q = useQuery({ queryKey: ['guides'], queryFn: () => getJson<GuideCard[]>('/api/guides'), enabled: !!member });
  const cards = member ? (q.data ?? []) : [PUBLIC_CARD];
  return (
    <div className="gd-wrap">
      <h1><span aria-hidden="true">❓</span> Guides</h1>
      <p className="page-sub">
        How to use the Research Hub, by role.
        {member && <> You are shown the guides for your role (<b>{rolesText(member)}</b>).</>}
      </p>
      {q.isError && <p className="error">{q.error.message}</p>}
      {cards.map((g) =>
        g.url ? (
          <a key={g.key} className="gd-card" href={g.url} target="_blank" rel="noopener noreferrer">
            <CardBody g={g} />
          </a>
        ) : (
          <Link key={g.key} className="gd-card" to="/guides/$doc" params={{ doc: g.key }}>
            <CardBody g={g} />
          </Link>
        ),
      )}
    </div>
  );
}

export function GuidePage({ doc }: { doc: string }) {
  const me = useMe();
  // Nobody signed in (me is null): these guides are for signed-in roles, so the API would answer 401.
  const visitor = me.data === null;
  const q = useQuery({
    queryKey: ['guides', doc],
    queryFn: () => getJson<{ title: string; html: string }>(`/api/guides/${encodeURIComponent(doc)}`),
    enabled: !visitor,
  });
  if (visitor) {
    return (
      <div className="gd-wrap">
        <p className="empty">
          Sign in to see this. <a href={signInUrl()}>Sign in</a>
        </p>
      </div>
    );
  }
  if (q.isPending) return <div className="gd-wrap"><p className="empty">Loading…</p></div>;
  if (q.isError) return <div className="gd-wrap"><p className="empty error">{q.error.message}</p></div>;
  return (
    <div className="gd-wrap">
      <p className="gd-back"><Link to="/guides">← All guides</Link></p>
      {/* Our own text, from the API — not user input. */}
      <article className="guide" dangerouslySetInnerHTML={{ __html: q.data.html }} />
    </div>
  );
}
